import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useMediaQuery, useTheme } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';

import { clearAutosavedDraft } from '../action/autosave/clear-autosaved-draft';
import { saveComment } from '../comments/actions/save-comment';
import { sendText } from '../contacts/actions/send-text';
import { fullApplicationSelector } from '../reducers/application-selectors';
import {
  draftCommentForJobApplicantSelector,
  draftMessageForJobApplicantSelector,
} from '../reducers/autosave-selectors';

import { fullScreenDialogStyles } from '../styles/full-screen-dialog-styles';
import { UiDialog } from '../ui/ui-dialog';
import { jobApplicationPageRoute } from '../web-routes';

import { AsyncApplicationPage } from './async-application-page';

import { DOMAIN } from '../config/config';
import { AUTOSAVED_DRAFT_TYPES } from '../util/autosaved-draft-types';
import { CkEditorNewlineTrimmer } from '../util/ckeditor-newline-trimmer';
import { formatStringMaxLength } from '../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { MESSAGE_TYPE } from '../util/message-types';
import { confirm } from '../widgets/confirmation-dialog';
import { TransitionSlideUp } from '../widgets/transition-slide-up';

type InputProps = {
  jobApplicantId: number;
  close: () => void;
  selectedTab?: string | null;
  prevApp?: () => void;
  nextApp?: () => void;
};

export const ApplicationPopUp = ({
  jobApplicantId,
  close,
  selectedTab,
  prevApp,
  nextApp,
}: InputProps) => {
  const [appLinkEl, setAppLinkEl] = useState<Element | null>(null);
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const draftComment = useSelector((state: any) =>
    draftCommentForJobApplicantSelector(state, jobApplicantId),
  );
  const draftMessage = useSelector((state: any) =>
    draftMessageForJobApplicantSelector(state, jobApplicantId),
  );
  const applicant = useSelector((state: any) => fullApplicationSelector(state, jobApplicantId));

  const theme = useTheme();
  const onMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFullscreen = onMobile || fullScreen;
  const classes = fullScreenDialogStyles();

  const showAppLink = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAppLinkEl(event.target as Element);
  };

  const hideAppLink = () => {
    setAppLinkEl(null);
  };

  const getLink = () => `${DOMAIN}${jobApplicationPageRoute(jobApplicantId)}`;

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  // https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
  const copyLink = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    const target = e.currentTarget;
    const textArea = document.createElement('textarea');
    textArea.value = getLink();
    if (target.parentNode) {
      target.parentNode.replaceChild(textArea, target);
    }
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    const parent = textArea.parentNode;
    if (parent) {
      parent.replaceChild(target, textArea);
    }
  };

  const confirmSaveOrLeaveDrafts = () => {
    // If there is a draft comment/message, we want to remind the user to save it to the db before closing the applicant profile
    const draftCommentText = draftComment?.messageBody;
    const draftMessageText = draftMessage?.messageBody;

    if (!draftCommentText && !draftMessageText) {
      // if there are no drafts, we don't need to ask the user to confirm save
      return false;
    }

    const confirmButtonLabel = `${draftCommentText ? 'Save' : ''}
    ${draftCommentText && draftMessageText ? ' and ' : ''}
    ${draftMessageText ? 'Send' : ''}`;

    return confirm(
      `You have a draft ${draftCommentText ? 'comment ' : ''}
      ${draftCommentText && draftMessageText ? 'and ' : ''}
      ${draftMessageText ? 'message' : ''}
      for this applicant. Would you like us to
      ${draftCommentText ? 'save the comment ' : ''}
      ${draftCommentText && draftMessageText ? 'and ' : ''}
      ${draftMessageText ? 'send the message' : ''} for you now?`,
      { cancelButtonLabel: 'Leave As Draft', confirmButtonLabel, title: 'Unsaved Drafts' },
    );
  };

  const saveAndClearDrafts = () => {
    if (draftComment?.messageBody) {
      dispatch(saveComment({ jobApplicantId }, draftComment?.messageBody));
      dispatch(clearAutosavedDraft(AUTOSAVED_DRAFT_TYPES.COMMENT, { jobApplicantId }));
    }
    if (draftMessage?.messageBody) {
      let message = draftMessage.messageBody;
      if (draftMessage.messageType === MESSAGE_TYPE.TEXT) {
        message = CkEditorNewlineTrimmer.trimMessage(message);
      }
      dispatch(
        sendText(
          applicant.contactId,
          { jobApplicantId },
          message,
          draftMessage.messageType || MESSAGE_TYPE.TEXT,
          draftMessage.subject || null,
          {
            jobId: applicant.job_id,
            orgId: applicant.orgId,
          },
        ),
      );
      dispatch(clearAutosavedDraft(AUTOSAVED_DRAFT_TYPES.MESSAGE, { jobApplicantId }));
    }
  };

  const onClose = async () => {
    const confirmedSave = await confirmSaveOrLeaveDrafts();
    if (confirmedSave) {
      saveAndClearDrafts();
    }
    close();
  };

  const navigate = (navFunction?: () => void) => async () => {
    const confirmedSave = await confirmSaveOrLeaveDrafts();
    if (confirmedSave) {
      saveAndClearDrafts();
    }
    if (navFunction) {
      navFunction();
    }
  };

  return (
    <div>
      <UiDialog
        onClose={onClose}
        transitionComponent={TransitionSlideUp}
        className={classes.dialogPaper}
        size="md"
        // Set the width to full screen onMobile
        fullScreen={isFullscreen}
        titleAction={
          <IconButton
            className="u-color-white u-padding-none"
            onClick={showAppLink}
            aria-label="View application link"
          >
            <MaterialDesignIcon iconName={ICON_NAMES.LINK} />
          </IconButton>
        }
        fullScreenAction={
          <IconButton
            className="u-color-white u-padding-none u-margin-right-md"
            onClick={toggleFullScreen}
            aria-label="Close"
          >
            {onMobile ? null : isFullscreen ? (
              <MaterialDesignIcon iconName={ICON_NAMES.FULLSCREEN_EXIT} />
            ) : (
              <MaterialDesignIcon iconName={ICON_NAMES.FULLSCREEN} />
            )}
          </IconButton>
        }
      >
        <UiDialog.Content>
          <AsyncApplicationPage
            jobApplicantId={jobApplicantId}
            tab={selectedTab}
            prevApp={prevApp ? navigate(prevApp) : undefined}
            nextApp={nextApp ? navigate(nextApp) : undefined}
            isFullscreen={isFullscreen}
          />
        </UiDialog.Content>
      </UiDialog>
      <Popover
        open={appLinkEl !== null}
        anchorEl={appLinkEl}
        onClose={hideAppLink}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <a
          href={getLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="u-verticalAlgin-middle"
          aria-label="Open"
        >
          <MaterialDesignIcon
            iconName={ICON_NAMES.OPEN_IN_NEW}
            className="u-verticalAlign-middle"
          />
        </a>
        <Tooltip title="Click to copy" placement="bottom">
          <div
            role="presentation"
            onClick={copyLink}
            className="u-verticalAlign-middle u-display-inline u-cursor-pointer u-margin-left-sm"
          >
            {formatStringMaxLength(getLink(), 40)}
          </div>
        </Tooltip>
      </Popover>
    </div>
  );
};
